import { useState } from 'react';
import type { GameState } from '@flip7/shared';
import { Card } from './Card';
import { Avatar } from './Avatar';
import { FlippableCard } from './FlippableCard';

interface DigitalModeControlsProps {
  gameState: GameState;
  playerId: string | null;
  currentPlayer: GameState['players'][number] | undefined;
  onChooseTarget: (targetPlayerId: string) => void;
  onSelectSwapCards?: (sourcePlayerId: string, sourceCardIndex: number, targetPlayerId: string, targetCardIndex: number) => void;
  onSelectStealTarget?: (targetPlayerId: string, cardIndex: number) => void;
  onSelectDiscardTarget?: (targetPlayerId: string, cardIndex: number) => void;
  onForfeitAction?: () => void;
}

/** Swap selection: pick one of your cards, then one of target's cards */
function SwapSelectionUI({
  gameState,
  drawer,
  isDrawer,
  canChoose,
  activePlayers,
  playerId,
  onSelectSwapCards,
}: {
  gameState: GameState;
  drawer: GameState['players'][number] | undefined;
  isDrawer: boolean;
  canChoose: boolean;
  activePlayers: GameState['players'][number][];
  playerId: string | null;
  onSelectSwapCards?: (sourcePlayerId: string, sourceCardIndex: number, targetPlayerId: string, targetCardIndex: number) => void;
}) {
  const [sourceIdx, setSourceIdx] = useState<number | null>(null);
  const [targetPlayerId, setTargetPlayerId] = useState<string | null>(null);

  const drawerCards = drawer?.currentRound?.cards ?? [];
  const numberCards = drawerCards.map((c, i) => ({ card: c, index: i })).filter(({ card }) => card.type === 'number');
  const targetPlayer = targetPlayerId ? activePlayers.find(p => p.id === targetPlayerId) : null;
  const targetCards = targetPlayer?.currentRound?.cards ?? [];
  const targetNumberCards = targetCards.map((c, i) => ({ card: c, index: i })).filter(({ card }) => card.type === 'number');

  return (
    <div className="turn-entrance turn-panel">
      <div className="ornate-border turn-panel__body" style={{ borderColor: '#00897B' }}>
        <p className="turn-panel__label">Action Card <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
        <p className="turn-panel__title" style={{ color: '#00897B' }}>Swap!</p>
        {!canChoose ? (
          <p className="turn-panel__sub">Waiting for {drawer?.name} to swap cards...</p>
        ) : sourceIdx === null ? (
          <>
            <p className="turn-panel__sub">
              {isDrawer ? 'Pick one of your number cards to swap:' : `Pick one of ${drawer?.name}'s number cards:`}
            </p>
            <div className="center-hand turn-panel__cards">
              {numberCards.map(({ card, index }) => (
                <button key={index} onClick={() => setSourceIdx(index)} className="card-btn">
                  <Card card={card} />
                </button>
              ))}
            </div>
          </>
        ) : !targetPlayerId ? (
          <>
            <p className="turn-panel__sub">Now pick a player to swap with:</p>
            <div className="target-list">
              {(() => {
                const opponents = activePlayers.filter(p => p.id !== drawer?.id);
                return (opponents.length > 0 ? opponents : activePlayers);
              })().map(player => (
                <button key={player.id} className="btn btn-secondary target-btn" onClick={() => setTargetPlayerId(player.id)}>
                  <span className="target-btn__name">{player.name}</span>
                  {player.id === playerId && <span className="target-btn__tag">You</span>}
                </button>
              ))}
            </div>
            <button className="btn btn-secondary btn-small turn-panel__back" onClick={() => setSourceIdx(null)}>Back</button>
          </>
        ) : (
          <>
            <p className="turn-panel__sub-bold">{targetPlayer?.name}'s cards:</p>
            <div className="center-hand turn-panel__cards">
              {targetNumberCards.map(({ card, index }) => (
                <button key={index} onClick={() => {
                  if (drawer && onSelectSwapCards) onSelectSwapCards(drawer.id, sourceIdx, targetPlayerId, index);
                }} className="card-btn">
                  <Card card={card} />
                </button>
              ))}
            </div>
            <button className="btn btn-secondary btn-small turn-panel__back" onClick={() => setTargetPlayerId(null)}>Back</button>
          </>
        )}
      </div>
    </div>
  );
}

/** Steal/Discard selection: pick a card from another player */
function CardTargetSelectionUI({
  actionName,
  actionColor,
  description,
  gameState,
  canChoose,
  targetPlayers,
  onSelect,
  onForfeit,
}: {
  actionName: string;
  actionColor: string;
  description: string;
  gameState: GameState;
  canChoose: boolean;
  targetPlayers: GameState['players'][number][];
  onSelect?: (targetPlayerId: string, cardIndex: number) => void;
  onForfeit?: () => void;
}) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const targetPlayer = selectedPlayerId ? targetPlayers.find(p => p.id === selectedPlayerId) : null;
  const targetCards = targetPlayer?.currentRound?.cards ?? [];
  const targetNumberCards = targetCards.map((c, i) => ({ card: c, index: i })).filter(({ card }) => card.type === 'number');

  return (
    <div className="turn-entrance turn-panel">
      <div className="ornate-border turn-panel__body" style={{ borderColor: actionColor }}>
        <p className="turn-panel__label">Action Card <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
        <p className="turn-panel__title" style={{ color: actionColor }}>{actionName}!</p>
        <p className="turn-panel__sub">{description}</p>

        {canChoose && onForfeit && targetPlayers.length === 0 && (
          <button className="btn btn-secondary turn-panel__continue" onClick={onForfeit}>Continue</button>
        )}

        {canChoose && !selectedPlayerId && targetPlayers.length > 0 && (
          <div className="target-list">
            {targetPlayers.map(player => (
              <button key={player.id} className="btn btn-secondary target-btn" onClick={() => setSelectedPlayerId(player.id)}>
                <span className="target-btn__name">{player.name}</span>
                <span className="target-btn__tag">{player.currentRound?.cards.filter(c => c.type === 'number').length ?? 0} cards</span>
              </button>
            ))}
          </div>
        )}

        {canChoose && selectedPlayerId && targetPlayer && (
          <>
            <p className="turn-panel__sub-bold">{targetPlayer.name}'s cards:</p>
            <div className="center-hand turn-panel__cards">
              {targetNumberCards.map(({ card, index }) => (
                <button key={index} onClick={() => {
                  if (onSelect) onSelect(selectedPlayerId, index);
                }} className="card-btn">
                  <Card card={card} />
                </button>
              ))}
            </div>
            <button className="btn btn-secondary btn-small turn-panel__back" onClick={() => setSelectedPlayerId(null)}>Back</button>
          </>
        )}
      </div>
    </div>
  );
}

export function DigitalModeControls({
  gameState,
  playerId,
  currentPlayer,
  onChooseTarget,
  onSelectSwapCards,
  onSelectStealTarget,
  onSelectDiscardTarget,
  onForfeitAction,
}: DigitalModeControlsProps) {
  const { turnPhase, pendingAction, flipThreeProgress } = gameState;
  const isMyTurn = currentPlayer?.id === playerId;
  const canAct = isMyTurn;
  const isDealingPhase = gameState.dealingToIndex === gameState.currentPlayerIndex
    && gameState.players.every(p => (p.currentRound?.cards.length ?? 0) <= 1);

  // Dealing phase
  if (turnPhase === 'dealing' || (turnPhase === 'card-dealt' && isDealingPhase)) {
    const dealingTo = gameState.players[gameState.dealingToIndex];
    const dealingCards = dealingTo?.currentRound?.cards ?? [];
    const lastCard = dealingCards.length > 0 ? dealingCards[dealingCards.length - 1] : null;
    const isNewlyDealt = turnPhase === 'card-dealt';
    return (
      <div className="turn-panel">
        <div className="ornate-border turn-panel__body">
          <p className="turn-panel__label">Dealing <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
          {dealingTo && (
            <p className="turn-panel__player-name turn-panel__player-name--maroon">
              {dealingTo.id === playerId ? 'Dealing to you' : `Dealing to ${dealingTo.name}`}
            </p>
          )}
          {lastCard && (
            <div className="center-hand turn-panel__cards">
              <FlippableCard
                key={`deal-${dealingTo?.id}-${dealingCards.length}`}
                card={lastCard}
                initialFaceDown={isNewlyDealt}
                faceDownHold={150}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Awaiting target selection for action card
  if (turnPhase === 'awaiting-target' && pendingAction) {
    const actionNameMap: Record<string, string> = {
      freeze: 'Freeze', 'flip-three': 'Flip Three', 'second-chance': 'Second Chance',
      'flip-four': 'Flip Four', 'just-one-more': 'Just One More',
    };
    const actionColorMap: Record<string, string> = {
      freeze: 'var(--color-freeze)', 'flip-three': 'var(--gold-dim)', 'second-chance': 'var(--color-bust)',
      'flip-four': '#E65100', 'just-one-more': '#00695C',
    };
    const actionName = actionNameMap[pendingAction.type] ?? pendingAction.type;
    const actionColor = actionColorMap[pendingAction.type] ?? 'var(--gold-dim)';

    const drawer = gameState.players.find(p => p.id === pendingAction.drawerId);
    const isDrawer = pendingAction.drawerId === playerId;
    const canChoose = isDrawer;

    const activePlayers = gameState.players.filter(p => {
      if (p.currentRound?.status !== 'active') return false;
      if (pendingAction.type === 'second-chance') {
        return !p.currentRound.cards.some(c => c.type === 'action' && c.value === 'second-chance');
      }
      return true;
    });

    return (
      <div className="turn-entrance turn-panel">
        <div className="ornate-border turn-panel__body" style={{ borderColor: actionColor }}>
          <p className="turn-panel__label">Action Card <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
          <p className="turn-panel__title" style={{ color: actionColor }}>{actionName}!</p>
          <p className="turn-panel__sub">
            {canChoose
              ? `${isDrawer ? 'You drew' : `${drawer?.name} drew`} ${actionName}. Choose a target:`
              : `Waiting for ${drawer?.name} to choose a target...`}
          </p>
        </div>

        {canChoose && (
          <div className="target-list">
            {activePlayers.map(player => (
              <button
                key={player.id}
                className="btn btn-secondary target-btn"
                onClick={() => onChooseTarget(player.id)}
              >
                <span className="target-btn__name">{player.name}</span>
                {player.id === playerId && <span className="target-btn__tag">You</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Flip Three in progress
  if (turnPhase === 'resolving-flip-three' && flipThreeProgress) {
    const target = gameState.players.find(p => p.id === flipThreeProgress.targetId);
    const targetCards = target?.currentRound?.cards ?? [];
    const flipThreeStartIdx = targetCards.length - flipThreeProgress.cardsDealt;
    return (
      <div className="turn-panel">
        <div className="ornate-border turn-panel__body" style={{ borderColor: 'var(--gold)' }}>
          <p className="turn-panel__label">Action <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
          <p className="turn-panel__title" style={{ color: 'var(--gold)' }}>Flip Three!</p>
          <p className="turn-panel__sub-bold">
            {target?.id === playerId ? 'You receive' : `${target?.name} receives`} cards...
          </p>
          <div className="flip-slots">
            {[0, 1, 2].map(n => {
              const cardIdx = flipThreeStartIdx + n;
              const card = n < flipThreeProgress.cardsDealt ? targetCards[cardIdx] : null;
              const isLatest = n === flipThreeProgress.cardsDealt - 1;
              return (
                <div key={n} className="flip-slot">
                  {card ? (
                    <div className="center-hand flip-slot__card">
                      <FlippableCard
                        key={`ft-${n}-${card.type}-${card.value}`}
                        card={card}
                        initialFaceDown={isLatest}
                        faceDownHold={200}
                      />
                    </div>
                  ) : (
                    <div className="flip-slot__empty">{n + 1}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Resolving Flip Four
  if (turnPhase === 'resolving-flip-four' && flipThreeProgress) {
    const target = gameState.players.find(p => p.id === flipThreeProgress.targetId);
    const targetCards = target?.currentRound?.cards ?? [];
    const flipStartIdx = targetCards.length - flipThreeProgress.cardsDealt;
    const totalCards = flipThreeProgress.cardsTotal ?? 4;
    return (
      <div className="turn-panel">
        <div className="ornate-border turn-panel__body" style={{ borderColor: '#E65100' }}>
          <p className="turn-panel__label">Action <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
          <p className="turn-panel__title" style={{ color: '#E65100' }}>Flip Four!</p>
          <p className="turn-panel__sub-bold">
            {target?.id === playerId ? 'You receive' : `${target?.name} receives`} cards...
          </p>
          <div className="flip-slots flip-slots--four">
            {Array.from({ length: totalCards }).map((_, n) => {
              const cardIdx = flipStartIdx + n;
              const card = n < flipThreeProgress.cardsDealt ? targetCards[cardIdx] : null;
              const isLatest = n === flipThreeProgress.cardsDealt - 1;
              return (
                <div key={n} className="flip-slot flip-slot--small">
                  {card ? (
                    <div className="center-hand flip-slot__card">
                      <FlippableCard key={`ff-${n}-${card.type}-${card.value}`} card={card} initialFaceDown={isLatest} faceDownHold={200} />
                    </div>
                  ) : (
                    <div className="flip-slot__empty">{n + 1}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Awaiting swap card selection
  if (turnPhase === 'awaiting-swap-selection' && pendingAction) {
    const drawer = gameState.players.find(p => p.id === pendingAction.drawerId);
    const isDrawer = pendingAction.drawerId === playerId;
    const canChoose = isDrawer;
    const activePlayers = gameState.players.filter(p => p.currentRound?.status === 'active');
    const drawerNumberCards = drawer?.currentRound?.cards.filter(c => c.type === 'number') ?? [];

    const opponentsWithCards = activePlayers.filter(p => p.id !== pendingAction.drawerId && p.currentRound?.cards.some(c => c.type === 'number'));
    if (opponentsWithCards.length === 0 && drawerNumberCards.length < 2 && canChoose) {
      return (
        <div className="turn-entrance turn-panel">
          <div className="ornate-border turn-panel__body" style={{ borderColor: '#00897B' }}>
            <p className="turn-panel__label">Action Card <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
            <p className="turn-panel__title" style={{ color: '#00897B' }}>Swap!</p>
            <p className="turn-panel__sub">No valid targets — action forfeited.</p>
            {onForfeitAction && (
              <button className="btn btn-secondary turn-panel__continue" onClick={onForfeitAction}>Continue</button>
            )}
          </div>
        </div>
      );
    }

    return (
      <SwapSelectionUI
        gameState={gameState}
        drawer={drawer}
        isDrawer={isDrawer}
        canChoose={canChoose}
        activePlayers={activePlayers}
        playerId={playerId}
        onSelectSwapCards={onSelectSwapCards}
      />
    );
  }

  // Awaiting steal target selection
  if (turnPhase === 'awaiting-steal-selection' && pendingAction) {
    const drawer = gameState.players.find(p => p.id === pendingAction.drawerId);
    const isDrawer = pendingAction.drawerId === playerId;
    const canChoose = isDrawer;
    const otherActive = gameState.players.filter(p => p.currentRound?.status === 'active' && p.id !== pendingAction.drawerId && p.currentRound!.cards.some(c => c.type === 'number'));
    const selfPlayer = otherActive.length === 0 ? gameState.players.filter(p => p.id === pendingAction.drawerId && p.currentRound?.status === 'active') : [];
    const targetPlayers = otherActive.length > 0 ? otherActive : selfPlayer;
    const isSelfOnly = otherActive.length === 0;
    const selfHasCards = selfPlayer.length > 0 && (selfPlayer[0].currentRound?.cards.some(c => c.type === 'number') ?? false);

    if (isSelfOnly && !selfHasCards) {
      return (
        <CardTargetSelectionUI
          actionName="Steal"
          actionColor="#6A1B9A"
          description={canChoose ? 'No valid targets — action forfeited.' : `Waiting for ${drawer?.name}...`}
          gameState={gameState}
          canChoose={canChoose}
          targetPlayers={[]}
          onSelect={onSelectStealTarget}
          onForfeit={onForfeitAction}
        />
      );
    }

    return (
      <CardTargetSelectionUI
        actionName="Steal"
        actionColor="#6A1B9A"
        description={canChoose ? (isSelfOnly ? 'No opponents have cards — you must target yourself:' : 'Pick a card to steal from another player:') : `Waiting for ${drawer?.name} to steal a card...`}
        gameState={gameState}
        canChoose={canChoose}
        targetPlayers={targetPlayers}
        onSelect={onSelectStealTarget}
      />
    );
  }

  // Awaiting discard target selection
  if (turnPhase === 'awaiting-discard-selection' && pendingAction) {
    const drawer = gameState.players.find(p => p.id === pendingAction.drawerId);
    const isDrawer = pendingAction.drawerId === playerId;
    const canChoose = isDrawer;
    const otherActive = gameState.players.filter(p => p.currentRound?.status === 'active' && p.id !== pendingAction.drawerId && p.currentRound!.cards.some(c => c.type === 'number'));
    const selfPlayer = otherActive.length === 0 ? gameState.players.filter(p => p.id === pendingAction.drawerId && p.currentRound?.status === 'active') : [];
    const targetPlayers = otherActive.length > 0 ? otherActive : selfPlayer;
    const isSelfOnly = otherActive.length === 0;
    const selfHasCards = selfPlayer.length > 0 && (selfPlayer[0].currentRound?.cards.some(c => c.type === 'number') ?? false);

    if (isSelfOnly && !selfHasCards) {
      return (
        <CardTargetSelectionUI
          actionName="Discard"
          actionColor="#B71C1C"
          description={canChoose ? 'No valid targets — action forfeited.' : `Waiting for ${drawer?.name}...`}
          gameState={gameState}
          canChoose={canChoose}
          targetPlayers={[]}
          onSelect={onSelectDiscardTarget}
          onForfeit={onForfeitAction}
        />
      );
    }

    return (
      <CardTargetSelectionUI
        actionName="Discard"
        actionColor="#B71C1C"
        description={canChoose ? (isSelfOnly ? 'No opponents have cards — you must target yourself:' : 'Pick a card to discard from another player:') : `Waiting for ${drawer?.name} to discard a card...`}
        gameState={gameState}
        canChoose={canChoose}
        targetPlayers={targetPlayers}
        onSelect={onSelectDiscardTarget}
      />
    );
  }

  // Flip 7 celebration
  if (turnPhase === 'flip7-result') {
    const playerCards = currentPlayer?.currentRound?.cards ?? [];
    return (
      <div className="turn-panel">
        <div className="ornate-border turn-panel__body turn-panel__body--gold">
          <p className="flip7-celebrate turn-panel__flip7-title">FLIP 7!</p>
          <p className="turn-panel__player-name turn-panel__player-name--maroon">{currentPlayer?.name}</p>
          <p className="turn-panel__sub">+15 bonus — round ends!</p>
          {playerCards.length > 0 && (
            <div className="center-hand turn-panel__cards">
              {playerCards.map((card, i) => (
                <div key={`${card.type}-${card.value}-${i}`} className="card-no-interact">
                  <Card card={card} />
                </div>
              ))}
            </div>
          )}
          <div className="turn-panel__score-line">
            Score: <strong className="flip7-celebrate turn-panel__score-highlight">{currentPlayer?.currentRound?.roundScore ?? 0}</strong>
          </div>
        </div>
      </div>
    );
  }

  // Bust result
  if (turnPhase === 'bust-result') {
    const playerCards = currentPlayer?.currentRound?.cards ?? [];
    const bustCard = currentPlayer?.currentRound?.bustCard;
    return (
      <div className="turn-panel">
        <div className="ornate-border turn-panel__body">
          <p className="turn-panel__label turn-panel__label--bust">Bust <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
          <p className="turn-panel__player-name">{currentPlayer?.name}</p>
          {bustCard && (
            <p className="turn-panel__bust-detail">
              Drew duplicate {bustCard.type === 'number' ? bustCard.value : bustCard.type}
            </p>
          )}
          {playerCards.length > 0 && (
            <div className="center-hand turn-panel__cards turn-panel__cards--faded">
              {playerCards.map((card, i) => (
                <div key={`${card.type}-${card.value}-${i}`} className="card-no-interact">
                  <Card card={card} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Awaiting decision OR card just dealt
  if (turnPhase === 'awaiting-decision' || (turnPhase === 'card-dealt' && !isDealingPhase)) {
    const isRevealing = turnPhase === 'card-dealt';
    const playerCards = currentPlayer?.currentRound?.cards ?? [];
    const lastIdx = playerCards.length - 1;
    return (
      <div className="turn-entrance turn-panel">
        <div
          className={`ornate-border turn-panel__body${isMyTurn ? ' turn-panel__body--spotlight' : ''}`}
          aria-live="polite"
        >
          <p className={`turn-panel__label${isMyTurn ? ' turn-panel__label--gold' : ''}`}>
            {isMyTurn ? 'Your Turn' : 'Current Turn'}
            <span className="turn-panel__deck">{gameState.deckRemaining} left</span>
          </p>

          <div className="turn-panel__identity">
            {currentPlayer && <Avatar config={currentPlayer.avatarConfig} emoji={currentPlayer.avatar} size={36} />}
            <p className={`turn-panel__player-name${isMyTurn ? ' turn-panel__player-name--maroon' : ''}`}>
              {currentPlayer?.name}
            </p>
          </div>

          {playerCards.length > 0 && (
            <div className="center-hand turn-panel__cards" style={{
              transform: playerCards.length > 6 ? `scale(${Math.max(0.55, 6 / playerCards.length)})` : undefined,
              transformOrigin: 'center center',
              marginBottom: playerCards.length > 6 ? `${-40 * (1 - Math.max(0.55, 6 / playerCards.length))}px` : undefined,
            }}>
              {playerCards.map((card, i) => (
                <div key={`${card.type}-${card.value}-${i}`} className="card-deal card-no-interact">
                  {isRevealing && i === lastIdx ? (
                    <FlippableCard card={card} initialFaceDown={true} />
                  ) : (
                    <Card card={card} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="turn-panel__score-line">
            Score: <strong className="turn-panel__score-value">{currentPlayer?.currentRound?.roundScore ?? 0}</strong>
          </div>

          {/* Hit/Stay buttons are rendered by GameBoardScreen as bottom-anchored actions */}
          {!(canAct && !currentPlayer?.isBot && currentPlayer?.currentRound?.status === 'active' && !isRevealing) && (
            <p className="turn-panel__waiting">
              {currentPlayer?.isBot ? `${'\uD83E\uDD16'} ${currentPlayer.name} is thinking...` : `Waiting for ${currentPlayer?.name}'s decision...`}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="ornate-border turn-panel__body">
      <p className="turn-panel__label">Round {gameState.currentRound} <span className="turn-panel__deck">{gameState.deckRemaining} left</span></p>
      <p className="turn-panel__waiting">Waiting for next turn...</p>
    </div>
  );
}
